// WO-GF-001-P1B-S27 · F117 — THE ONE CART BILLING CONTRACT.
//
// Extracted verbatim from `CartTray` (F123), unchanged in behaviour, because the F117 cart PAGE renders
// the same contract as the slide-out and the two must not be able to disagree. The founder's billing
// screenshot is one contract read on two surfaces: the mini-cart flyout (review-in-place) and the full
// page (review-before-checkout, the Amazon pattern he directed). If each surface owned its own copy of
// `lineTotal`/`lineSaving`/`editHref`, the page and the tray could print different money for the same
// basket — and the first person to notice would be the founder, in a walk.
//
// This module holds ONLY the arithmetic and the destination rule. Presentation stays in each surface,
// because they genuinely differ: the tray is a 360px column, the page is a two-column review layout.
//
// NOTHING HERE IS NEW. Same guest-price basis (so the crown-jewel totals do not move), same
// member-vs-guest savings derivation, same nav-chokepoint Edit resolution. F123's declared deltas carry
// over untouched: taxes remain unsourced (F85) and the Total therefore visibly excludes tax.

import type { CartItem, CartSection } from "@/lib/cart-store";
import { hudNavHref, mountainHubHref } from "@/components/site/hub-nav";

/**
 * Live prints whole amounts bare ("$94") and fractional ones to the cent ("$149.83").
 *
 * WO-COMMERCE-BIND-001 §25.6 — FOUNDER RULING, 2026-08-12: prices render **rounded, with no decimal
 * point**. This overrides the parity default above, deliberately: it is a display ruling the founder
 * made after walking the site, and "live does it this way" does not outrank him on his own design.
 *
 * ROUNDED, NOT TRUNCATED, and this matters. `Math.trunc` would render $149.83 as "$149" — a number
 * SMALLER than what is charged, which is the one direction a price may never be wrong in. Rounding is
 * the honest presentation of an amount whose true value lives in the arithmetic, not in the label.
 *
 * The ARITHMETIC is untouched. Every total, saving and line still computes on the real numbers; only
 * the rendering rounds. Nothing downstream reads a formatted string back as a value.
 */
export const money = (n: number) => `$${Math.round(n)}`;

/** The price a line is charged at. Guest price is the basis — unchanged since B3.4. */
export const linePrice = (i: CartItem) => i.guestPrice ?? 0;
export const lineTotal = (i: CartItem) => linePrice(i) * i.qty;

/**
 * F123 — live's "Snowclub savings": the member-vs-guest delta on what is in the cart. Real arithmetic
 * over two prices each line already carries, not a modelled discount. A line missing either price
 * contributes NOTHING rather than being assumed to have no saving.
 */
export const lineSaving = (i: CartItem) =>
  i.guestPrice != null && i.memberPrice != null && i.guestPrice > i.memberPrice
    ? (i.guestPrice - i.memberPrice) * i.qty
    : 0;

/** The top-level axis (F123): live's cart splits MARKETPLACE vs TRANSPORT — the My Tickets CATEGORY axis. */
export const sectionOf = (i: CartItem): CartSection => i.section ?? "marketplace";

/**
 * `Edit` destination for a section, resolved through the EXISTING nav chokepoints off the line's own
 * mountain — so it lands on an already-served page and cannot dangle. The destination is an inference
 * from a screenshot LABEL (the F119 rule / the F127-F128 lesson), and stays flagged for the founder walk.
 */
export const editHref = (key: CartSection, lines: CartItem[]): string | null => {
  const mountain = lines[0]?.mountain;
  if (!mountain) return null;
  return key === "marketplace" ? hudNavHref(mountain, "marketplace") : mountainHubHref(mountain);
};

/**
 * WO-GF-001-P1B-S30 · F178 — PER-LINE `Edit`: back to THAT line's own surface, with its selection.
 *
 * The founder's finding was "Edit lands on the home page". Two separate reasons, both here:
 *   1. `editHref` above is a SECTION rule. It answers "where does this whole section come from", so even
 *      when it resolved it landed on a mountain/marketplace HUB — not the page the product was chosen
 *      on — and nothing could be pre-selected there.
 *   2. It returns `null` when the first line has no mountain, and both call sites wrote `|| "/"`.
 *      That is the home page, literally.
 *
 * A line now carries `sourcePath` (F178, stamped in `addItem`), so the destination is the line's own
 * origin. The selection rides in the query string:
 *   • TRANSPORT lines — `sourcePath` already contains the funnel's own `startDate`/`endDate`/pax params,
 *     so it is returned UNTOUCHED. Re-deriving them would risk disagreeing with the URL that produced
 *     the line.
 *   • MARKETPLACE lines — the product surface is not date-parameterised in its own right, so the
 *     selection is appended explicitly: `edit` (the line id, so the surface knows which line it is
 *     amending), `startDate`, `endDate`, `qty`.
 *
 * REPLACE, NOT DUPLICATE, is guaranteed by line identity rather than by anything here: `lineId` in
 * cart-store is `listingId || mountain:supplierSlug:key` and contains NO date and NO quantity, so
 * re-adding the same product after changing its date upserts onto the same line. `edit` is carried so
 * the SURFACE can pre-select, not to control merging.
 *
 * Returns null only when the line has no origin AND its section rule cannot resolve either — the caller
 * then renders no Edit control at all, which is honest. It must never fall back to "/".
 */
export const lineEditHref = (i: CartItem): string | null => {
  if (i.sourcePath) {
    if (sectionOf(i) === "transport") return i.sourcePath;
    const [path, search = ""] = i.sourcePath.split("?");
    const p = new URLSearchParams(search);
    p.set("edit", i.id);
    if (i.startDate) p.set("startDate", i.startDate);
    if (i.endDate) p.set("endDate", i.endDate);
    if (i.qty) p.set("qty", String(i.qty));
    return `${path}?${p.toString()}`;
  }
  // S35 · F212 — NO recorded origin (a line persisted from before F178). This used to fall back to the
  // SECTION rule, which resolves to the mountain hub or the marketplace landing. The founder walked
  // exactly that destination on 2026-07-27 and called it useless: it is away-navigation with nothing
  // pre-selected, i.e. an Edit that does not edit. An originless line therefore resolves to NOTHING and
  // the caller draws no Edit control at all — the S29 dead-control rule. Honest absence beats a link
  // that lands somewhere the shopper did not ask to go.
  return null;
};

/**
 * WO-FOUNDER-WALK-UNBLOCK-001 · T1 — THE CART'S WAY FORWARD WHEN THERE IS NO DRAFT ORDER YET.
 *
 * THE FOUNDER'S REPORT, verbatim: he totals a cart and **there is no button**. Reproduced exactly on
 * :3021 at `77dc105` — `/cart` renders the total, and where PROCEED TO CHECKOUT belongs there is a
 * line of grey text: "Sign in from the cart to hold your booking before checkout."
 *
 * WHY, measured — and it is NOT the `/checkout/<id>` 404 that S35 already fixed:
 *   1. `checkoutHref` only resolves for a line that is already `booked` with an `orderDocumentId`.
 *   2. That id comes from `/api/marketplace/book-transport`, which §14 RULING 1 makes REFUSE in phase 1
 *      because only the legacy engine emails the operator (§10). The refusal is correct and stays.
 *   3. So on the surface he walks, no line can ever be booked, `firstOrder` is always null, and the
 *      control is never drawn. The cart was gated behind the one door phase 1 deliberately locked.
 *
 * AND THE CHECKOUT WAS THERE ALL ALONG. `/mountain-<code>/booking/review` mounts `ExportCheckout` —
 * the founder's OWN export pay flow, PAYMENT → DETAILS → REVIEW → CONFIRMED, fed from this same cart
 * and submitting to the legacy engine (§16/§18). Nothing links to it from the cart, which is the whole
 * of the defect: a built checkout with no door.
 *
 * The mountain comes from the CART'S OWN LINES, never a default. A cart with no mountain on any line
 * resolves to null and the caller draws no control — the S29 dead-control rule — because guessing
 * "Whistler" is the fallback-to-first-element fabrication pattern this program has already been bitten
 * by twice (§8 root cause, CLAUDE.md §11).
 */
export const funnelCheckoutHref = (items: CartItem[]): string | null => {
  const mountain = items.find((i) => i.mountain)?.mountain;
  return mountain ? `/mountain-${mountain}/booking/review` : null;
};

/**
 * SNOWCLUB-V101-STAGING-A1-ACCEPTANCE-001 — THE SIGNED-IN TRAY'S CHECKOUT DESTINATION.
 *
 * A basket holding ANY transport line goes to the design review for the TRANSPORT line's own mountain
 * (createDesignOrder → customer-cart-to-order → in-page Stripe), exactly as the signed-out tray already
 * did. It used to call `/api/marketplace/book-transport`, which writes an unfunded `pending_acceptance`
 * order and told the customer their seats were "held" — a booking with no payment path. The review page
 * reads the host cart, so marketplace lines in a mixed basket travel with it. No mountain on any
 * transport line → null (no default mountain).
 *
 * FOUNDER RULING 2026-09-26: a product-only basket no longer has a "draft-order hand-off" to the checkout
 * app — there is no such page. It goes to the review page of its own mountain like every other basket.
 */
export const trayCheckoutHref = (items: CartItem[]): string | null => {
  const transport = items.filter((i) => sectionOf(i) === "transport");
  return funnelCheckoutHref(transport.length > 0 ? transport : items);
};

export interface CartSectionView {
  key: CartSection;
  label: string;
  lines: CartItem[];
  subtotal: number;
}

/**
 * THE PARTITION — computed once, read twice: once for the sections, once for the billing subtotals.
 * That identity is the point of F123's axis, and it is what the F117 DoD asserts (per-section subtotals
 * must sum to the billing total on a mixed basket).
 */
export function cartSections(items: CartItem[]): CartSectionView[] {
  return (
    [
      { key: "marketplace" as CartSection, label: "MARKETPLACE" },
      { key: "transport" as CartSection, label: "TRANSPORT" },
    ] as const
  ).map((s) => {
    const lines = items.filter((i) => sectionOf(i) === s.key);
    return { key: s.key, label: s.label, lines, subtotal: lines.reduce((sum, i) => sum + lineTotal(i), 0) };
  });
}

/**
 * WO-GF-001-P1B-S30 · F175 — MOUNTAIN ATTRIBUTION AND GROUPING, decided ONCE, here.
 *
 * The founder's basket mixed mountains and the cart did not say so anywhere: every line rendered its
 * SUPPLIER but never which mountain the supplier was at, so two identical-looking rentals from two
 * resorts were indistinguishable, on both the tray and the page.
 *
 * THE GROUPING RULE (recorded as the WO asks): **mountain first, then the existing MARKETPLACE /
 * TRANSPORT section, then category.** Reasons, in order of weight:
 *   • A mountain is a TRIP. You cannot take a Whistler lift pass on a Big White trip, so mixing them is
 *     the error state worth making loud; mixing a rental and an activity at one resort is not.
 *   • Section is already the axis live splits on (F123) and the axis the billing subtotals reconcile
 *     against, so it must stay intact INSIDE a mountain rather than be re-ordered around it — that keeps
 *     the per-section subtotals meaningful and the page/tray arithmetic identical.
 *   • `category` remains the sub-axis it has been since F111, untouched.
 *
 * SINGLE-MOUNTAIN BASKETS ARE NOT REGROUPED. `byMountain` returns one group and the surfaces render no
 * mountain heading, so the overwhelmingly common basket looks exactly as it does today and no pixel
 * baseline moves. The grouping UI appears only when a basket actually mixes mountains — which is the
 * condition the founder hit.
 *
 * Order is by first appearance in the cart, not alphabetical: the traveller's own build order is the
 * least surprising, and it keeps a line from jumping groups when another mountain is added.
 */
export interface CartMountainGroup {
  mountain: string;
  label: string;
  lines: CartItem[];
  subtotal: number;
}

/** Display label for a mountain code. Falls back to the code itself rather than inventing a name. */
export const mountainLabelOf = (code: string): string =>
  code ? code.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "";

export function cartMountains(items: CartItem[]): CartMountainGroup[] {
  const order: string[] = [];
  for (const i of items) if (i.mountain && !order.includes(i.mountain)) order.push(i.mountain);
  return order.map((m) => {
    const lines = items.filter((i) => i.mountain === m);
    return {
      mountain: m,
      label: mountainLabelOf(m),
      lines,
      subtotal: lines.reduce((sum, i) => sum + lineTotal(i), 0),
    };
  });
}

/** True when the basket spans more than one mountain — the condition that turns grouping UI on. */
export const isMixedMountain = (items: CartItem[]): boolean => cartMountains(items).length > 1;

export interface CartTotals {
  itemsTotal: number;
  savings: number;
  /** lines + tip. EXCLUDES tax — "Taxes & Fees" has no source in this build (F85, declared). */
  total: number;
  hasTransport: boolean;
}

export function cartTotals(items: CartItem[], driverTip: number): CartTotals {
  const itemsTotal = items.reduce((sum, i) => sum + lineTotal(i), 0);
  return {
    itemsTotal,
    savings: items.reduce((sum, i) => sum + lineSaving(i), 0),
    total: itemsTotal + driverTip,
    hasTransport: items.some((i) => sectionOf(i) === "transport"),
  };
}

/**
 * FOUNDER-WALK-QA-STAGING-007 · D1 — THE ADD-TO-CART DATE PREDICATE, as ONE rule.
 *
 * THE DEFECT, named. `MarketplaceSupplierDetail`'s per-row "Add" was UNCONDITIONAL: `readRow()` read
 * `dateInputs[0]?.value || ""` and `dateInputs[1]?.value || ""` and the click handler pushed the line
 * whatever came back. A rental row carries TWO date fields and the captured markup ships the second one
 * EMPTY (`value=""` on "Ending:" — verified in the committed detail JSON), so the founder's own supplier
 * page would put a hire in the cart with no return date at all, and nothing on the surface said so.
 *
 * THE RULE ALREADY EXISTED — in `QuickViewLayer` (F97), as component-local state. Two surfaces adding
 * the same product to the same cart under two different rules is the same class of defect as the tray
 * and the page each owning their own `lineTotal` (the reason this module exists). So the predicate moves
 * here VERBATIM in behaviour — `missing`, `badRange`, `canAdd`, and the hint strings QuickViewLayer
 * already prints — and the detail page reads it. QuickViewLayer itself is NOT edited in this packet
 * (not owned); routing it onto this same import is the recorded follow-up.
 *
 * `requiresEnd` is the caller's own reading of its row, never an inference made here: QuickViewLayer
 * passes `supplier.variant === "equipment-rentals"`, and the detail page passes whether the ROW ITSELF
 * renders a second date field. Both are served facts about the thing being booked.
 *
 * NOT IN SCOPE, stated rather than silently handled: this does not reject a date in the PAST. The
 * captured rows ship a crawl-day start value (2026-07-19) which is now stale, so a past-date rule would
 * change what every at-rest row is allowed to do — a separate, founder-visible decision (see report).
 */
export interface DateGuardInput {
  /** the row's start/"Desired Date"/"Starting:" field value ("" when unset) */
  startDate: string;
  /** the row's "Ending:" field value ("" when the row has no such field, or it is unset) */
  endDate: string;
  /** does this row book a RANGE (a hire) rather than a single day? */
  requiresEnd: boolean;
}

export interface DateGuardResult {
  /** a required date has not been chosen */
  missing: boolean;
  /** both ends are chosen but the range runs backwards */
  badRange: boolean;
  /** the ONLY thing a caller should gate "Add" on */
  canAdd: boolean;
  /** why the add is unavailable — null when it is available. Never a dead-looking button (F97). */
  hint: string | null;
}

export function dateGuard({ startDate, endDate, requiresEnd }: DateGuardInput): DateGuardResult {
  const start = (startDate || "").trim();
  const end = (endDate || "").trim();
  const missing = !start || (requiresEnd && !end);
  const badRange = requiresEnd && !!start && !!end && end < start;
  const canAdd = !missing && !badRange;
  return {
    missing,
    badRange,
    canAdd,
    hint: canAdd
      ? null
      : badRange
        ? "The ending date cannot be before the starting date."
        : requiresEnd
          ? "Choose your starting and ending dates to add this to your cart."
          : "Choose a date to add this to your cart.",
  };
}
